<script setup lang="ts">
import { ref, computed } from 'vue'
import { isTool, isNode } from '@/types'
import type { ChatItem, NodeActivity, ToolActivity } from '@/types'
import NodeActivityBubble from './NodeActivityBubble.vue'
import ToolActivityBubble from './ToolActivityBubble.vue'

const props = defineProps<{ activities: ChatItem[] }>()

const expanded = ref(false)

const NODE_LABELS: Record<string, string> = {
  query_prep: 'Query Prep',
  tool_registry: 'Tool Registry',
  tag_extraction: 'Tag Extraction',
  resolver: 'Entity Resolver',
  router: 'Router',
  probe_executor: 'Probe',
  retrieval_planner: 'Retrieval Planner',
  parallel_retriever: 'Parallel Retriever',
  evidence_processor: 'Evidence Processor',
  composer: 'Composer',
}

function labelFor(item: ChatItem): string {
  if (isNode(item)) {
    const n = item as NodeActivity
    const name = NODE_LABELS[n.node] || n.node
    return n.detail ? `${name} — ${n.detail}` : name
  }
  if (isTool(item)) {
    const t = item as ToolActivity
    return t.detail || t.label || t.tool
  }
  return ''
}

// The latest step drives the collapsed header (it "overwrites" as steps arrive).
const latest = computed(() => props.activities[props.activities.length - 1])
const latestLabel = computed(() => (latest.value ? labelFor(latest.value) : ''))

const isRunning = computed(() =>
  props.activities.some(
    a => (isNode(a) && (a as NodeActivity).status === 'running')
      || (isTool(a) && (a as ToolActivity).status === 'executing'),
  ),
)
const hasError = computed(() =>
  props.activities.some(
    a => (isNode(a) && (a as NodeActivity).status === 'error')
      || (isTool(a) && (a as ToolActivity).status === 'error'),
  ),
)
</script>

<template>
  <div class="self-start max-w-[85%] w-full">
    <!-- Collapsed header: shows the latest step; click to expand all steps -->
    <button
      type="button"
      class="flex w-full items-center gap-2 rounded-md border-l-[3px] px-3 py-1.5 text-left text-xs transition-colors"
      :class="hasError
        ? 'border-red-400 bg-red-50 text-red-700 hover:bg-red-100'
        : isRunning
          ? 'border-indigo-400 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
          : 'border-neutral-300 bg-neutral-50 text-neutral-500 hover:bg-neutral-100'"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <span
        v-if="isRunning"
        class="inline-block h-1.5 w-1.5 shrink-0 animate-pulse-dot rounded-full bg-indigo-500"
      />
      <span v-else-if="hasError" class="shrink-0">&#10007;</span>
      <span v-else class="shrink-0 text-green-500">&#10003;</span>

      <span class="min-w-0 flex-1 truncate">{{ latestLabel || 'Working…' }}</span>

      <span class="shrink-0 text-[11px] text-neutral-400">{{ activities.length }} step{{ activities.length === 1 ? '' : 's' }}</span>

      <svg
        class="h-3.5 w-3.5 shrink-0 transition-transform"
        :class="expanded ? 'rotate-180' : ''"
        viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"
      >
        <path d="M6 8l4 4 4-4" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <!-- Expanded: the full ordered list of steps -->
    <div v-if="expanded" class="mt-1 flex flex-col pl-2">
      <template v-for="item in activities" :key="item.id">
        <NodeActivityBubble v-if="isNode(item)" :activity="item" />
        <ToolActivityBubble v-else-if="isTool(item)" :activity="item" />
      </template>
    </div>
  </div>
</template>
